import {Box, Button, Typography} from '@mui/material';
import {useAuth} from '@saltus/auth';
import {useSnackbar} from 'notistack';
import React, {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import MfaEnrolmentPanel from '../../components/Mfa/MfaEnrolmentPanel';
import PinEntry from '../../components/PinEntry/PinEntry';
import {themeColors} from '../../theme/Theme';

const classes = {
  button: {
    backgroundColor: themeColors.coral,
    border: `2px solid ${themeColors.coral}`,
    color: themeColors.white,
    fontWeight: 400,
    margin: 0,
    padding: '5px 60px',
    textTransform: 'none',
    '&:disabled': {
      color: themeColors.white,
      opacity: 0.3,
    },
    '&:hover': {
      backgroundColor: themeColors.backgroundDark,
      border: `2px solid ${themeColors.backgroundDark}`,
    },
  },

  label: {
    margin: '16px 0',
  },

  linkButton: {
    color: themeColors.coral,
    textDecoration: 'underline',
    textTransform: 'none',
  },

  strongText: {
    fontWeight: 600,
  },
};

interface AlertMessage {
  errors: {
    message: string;
  }[];
}

function MfaCodePage() {
  const {
    getUserAttributeVerificationCode,
    setPreferredMFA,
    user,
    verifyTotpToken,
    verifyUserAttributeSubmit,
  } = useAuth();
  const [button, setButton] = useState(true);
  const [code, setCode] = useState<string>();
  const [alertMessage, setAlertMessage] = useState<AlertMessage>();
  const navigate = useNavigate();
  const {pathway} = useParams();
  const {enqueueSnackbar} = useSnackbar();

  const getText = (app: string, sms: string) => (pathway === 'sms' ? sms : app);

  if (user) {
    return (
      <div>
        <MfaEnrolmentPanel
          title="Two-factor authentication"
          subTitle={getText('Authenticator app', 'Text message')}
          backTo={`/2fa-enrolment/${pathway}`}
          button={
            <Button
              sx={classes.button}
              type="button"
              disabled={button}
              variant="outlined"
              onClick={() => {
                if (pathway === 'app' && code) {
                  verifyTotpToken(code)
                    .then(() =>
                      setPreferredMFA({
                        totp: 'PREFERRED',
                      })
                    )
                    .then(() => {
                      navigate('/2fa-enrolment/enrolled/app');
                    })
                    .catch(() => {
                      setAlertMessage({
                        errors: [
                          {
                            message: 'Incorrect code entered',
                          },
                        ],
                      });
                    });
                } else if (pathway === 'sms' && code) {
                  verifyUserAttributeSubmit('phone_number', code)
                    .then(() =>
                      setPreferredMFA({
                        sms: 'PREFERRED',
                      })
                    )
                    .then(() => {
                      navigate('/2fa-enrolment/enrolled/sms');
                    })
                    .catch(() => {
                      setAlertMessage({
                        errors: [
                          {
                            message: 'Incorrect code entered',
                          },
                        ],
                      });
                    });
                }
              }}
            >
              Verify code
            </Button>
          }
          align="left"
        >
          <Typography sx={classes.strongText} variant="body1">
            Enter the code below
          </Typography>

          <Typography sx={classes.label} variant="body2">
            {getText(
              'The app should have generated a six digit code.',
              `We've sent an authentication code to ${user.attributes.phone_number}`
            )}
          </Typography>

          {alertMessage?.errors.length && (
            <Box>
              <ErrorMessage error={alertMessage.errors[0]} />
            </Box>
          )}

          <PinEntry
            focus
            length={6}
            onComplete={(value) => {
              setCode(value);
              setButton(false);
            }}
          />

          <Button
            sx={classes.linkButton}
            type="button"
            variant="text"
            onClick={() => {
              if (pathway === 'app') {
                navigate('/2fa-enrolment/app');
              } else if (pathway === 'sms') {
                getUserAttributeVerificationCode('phone_number').then(() => {
                  enqueueSnackbar('Code resent', {variant: 'success'});
                });
              }
            }}
          >
            {getText('Scan a new QR code', 'Resend code')}
          </Button>
        </MfaEnrolmentPanel>
      </div>
    );
  }

  return null;
}

export default MfaCodePage;
